import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Download, FolderOpen, LogOut } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import SyncIndicator from './SyncIndicator';
import SignOutDialog from './SignOutDialog';
import PlanBanner from './PlanBanner';
import AppUpdateBanner from './AppUpdateBanner';
import type { Lab } from '../lib/lab';
import { isNativeShell } from '../lib/nativeBridge';

export default function Layout({
  children,
  lab,
  orgRole = null,
}: {
  children: ReactNode;
  lab: Lab | null;
  orgRole?: 'owner' | 'admin' | 'member' | null;
}) {
  const { user } = useAuth();
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  // Someone already inside the installed app has nothing to download.
  const showDownload = !isNativeShell();

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-30 border-b border-ink-800 bg-ink-950/90 backdrop-blur"
        // The packaged app already positions the WebView below the status bar,
        // so applying the inset here too left a dead band across the top.
        style={isNativeShell() ? undefined : { paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-2.5">
          <Link to="/" className="inline-flex items-center text-lg font-semibold tracking-tight pointer-coarse:min-h-11">
            Blisx<span className="text-accent-400">Bench</span>
          </Link>
          {lab && (
            <span
              className="hidden truncate text-sm text-ink-400 md:inline"
              title={lab.code ? `Lab code ${lab.code}` : undefined}
            >
              {lab.name}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <SyncIndicator />
            {lab?.code && (
              <Link
                to="/updates"
                className="inline-flex items-center gap-1.5 rounded-lg border border-ink-700 px-2.5 py-1.5 text-xs font-medium text-ink-300 transition-colors hover:border-ink-600 hover:text-ink-100 pointer-coarse:min-h-11"
                title="Progress updates"
              >
                <FolderOpen size={14} />
                <span className="hidden sm:inline">Updates</span>
              </Link>
            )}
            {(orgRole === 'owner' || orgRole === 'admin') && (
              <Link
                to="/org"
                className="inline-flex items-center gap-1.5 rounded-lg border border-ink-700 px-2.5 py-1.5 text-xs font-medium text-ink-300 transition-colors hover:border-ink-600 hover:text-ink-100 pointer-coarse:min-h-11"
                title="Organisation"
              >
                <Building2 size={14} />
                <span className="hidden sm:inline">Organisation</span>
              </Link>
            )}
            <span className="hidden max-w-40 truncate text-sm text-ink-400 lg:inline">{user?.displayName ?? user?.email}</span>
            {showDownload && (
              <a
                href="/download"
                className="inline-flex items-center gap-1.5 rounded-lg border border-ink-700 px-2.5 py-1.5 text-xs font-medium text-ink-300 transition-colors hover:border-ink-600 hover:text-ink-100 pointer-coarse:min-h-11"
                title="Download the app"
              >
                <Download size={14} />
                <span>Download App</span>
              </a>
            )}
            <button
              onClick={() => setConfirmingSignOut(true)}
              className="icon-btn text-ink-400 transition-colors hover:bg-ink-800 hover:text-ink-100"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>
      <AppUpdateBanner />
      <PlanBanner code={lab?.code ?? null} orgRole={orgRole} />
      <main className="mx-auto max-w-6xl px-4 py-6">{children}</main>
      <SignOutDialog open={confirmingSignOut} onClose={() => setConfirmingSignOut(false)} />
    </div>
  );
}
